import type { BillCalculation, BillLine } from "@luckymenu/billing-core";
import type { BillKind } from "@luckymenu/core";

/**
 * A bill as staff read it, on the bill screen, the payment screen, a bill's
 * page and the settings preview: the rows between the item total and the
 * total. Labels are keys the apps translate from `billing:rows.<labelKey>`
 * (with `{{rate}}`); a discount's own label is shown as it is. Every amount
 * comes from a calculation by billing-core.
 */

export type BillRowLabel =
	| "itemTotal"
	| "itemDiscounts"
	| "discount"
	| "serviceCharge"
	| "cgst"
	| "sgst"
	| "roundOff";

export type BillRow = {
	key: string;
	labelKey: BillRowLabel;
	/** A percentage for `serviceCharge`, `cgst` and `sgst`, such as "2.5". */
	rate: string | null;
	/** The bill discount's own label, shown instead of the translated one. */
	label: string | null;
	/** Negative for discounts. */
	paise: number;
};

/**
 * Basis points as a percentage without trailing zeros: 250 → "2.5",
 * 500 → "5", and half of an odd rate (CGST, SGST) 12.5 → "0.125".
 */
export function percent(bp: number): string {
	const thousandths = Math.round(bp * 10);
	const whole = Math.trunc(thousandths / 1000);
	const rest = thousandths % 1000;
	if (rest === 0) return String(whole);
	return `${whole}.${String(rest).padStart(3, "0").replace(/0+$/, "")}`;
}

const row = (
	key: string,
	labelKey: BillRowLabel,
	paise: number,
	extra: { rate?: string; label?: string | null } = {},
): BillRow => ({ key, labelKey, paise, rate: extra.rate ?? null, label: extra.label || null });

/**
 * Item total, item discounts together, the bill discount, service charge,
 * CGST and SGST for each taxed rate (regular GST only), and round-off.
 * Rows that are zero are left out, except the item total.
 */
export function billRows(calculation: BillCalculation): BillRow[] {
	const { totals } = calculation;
	const rows = [row("items", "itemTotal", totals.subtotalPaise)];
	if (totals.itemDiscountPaise > 0)
		rows.push(row("itemDiscounts", "itemDiscounts", -totals.itemDiscountPaise));
	if (totals.billDiscountPaise > 0)
		rows.push(
			row("billDiscount", "discount", -totals.billDiscountPaise, {
				label: calculation.billDiscount?.label ?? null,
			}),
		);
	if (totals.serviceChargePaise > 0)
		rows.push(
			row("serviceCharge", "serviceCharge", totals.serviceChargePaise, {
				rate: percent(calculation.serviceCharge?.rateBp ?? 0),
			}),
		);
	if (calculation.gstMode === "regular") {
		for (const tax of calculation.taxes) {
			if (tax.cgstPaise === 0 && tax.sgstPaise === 0) continue;
			const rate = percent(tax.rateBp / 2);
			rows.push(
				row(`cgst-${tax.rateBp}`, "cgst", tax.cgstPaise, { rate }),
				row(`sgst-${tax.rateBp}`, "sgst", tax.sgstPaise, { rate }),
			);
		}
	}
	if (totals.roundOffPaise !== 0) rows.push(row("roundOff", "roundOff", totals.roundOffPaise));
	return rows;
}

/** "Prices include GST." belongs under a bill whose prices had the tax taken out of them. */
export function taxIncluded(calculation: BillCalculation): boolean {
	return (
		calculation.gstMode === "regular" &&
		calculation.pricesIncludeTax &&
		calculation.taxes.some((t) => t.cgstPaise > 0)
	);
}

/** The document's name, translated from `billing:kinds.<key>`. */
export function billKindKey(kind: BillKind): "taxInvoice" | "billOfSupply" | "bill" {
	if (kind === "tax_invoice") return "taxInvoice";
	if (kind === "bill_of_supply") return "billOfSupply";
	return "bill";
}

/**
 * Why a line costs less, in words: the staff discount's reason, or the
 * offer's name (an offer has a label and no reason). Null when neither.
 */
export function lineDiscountWhy(line: Pick<BillLine, "discount">): string | null {
	return line.discount?.reason || line.discount?.label || null;
}

/** A line's name with its size: "Biryani (Half)". */
export function billLineName(line: Pick<BillLine, "name" | "variantName">): string {
	return line.variantName ? `${line.name} (${line.variantName})` : line.name;
}
